import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createStickyNote,
  loadStickyNotes,
  saveStickyNotes,
} from './stickyNotesStorage';
import {
  STICKY_NOTES_MAX,
  STICKY_NOTE_WIDTH,
  type StickyNote,
  type StickyNoteColor,
} from './types';

function nextZIndex(notes: StickyNote[]): number {
  if (notes.length === 0) return 1;
  return Math.max(...notes.map(n => n.zIndex)) + 1;
}

function defaultPosition(existingCount: number): { x: number; y: number } {
  const offset = (existingCount % 8) * 24;
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  return {
    x: Math.max(24, Math.min(viewportW / 2 - STICKY_NOTE_WIDTH / 2 + offset, viewportW - STICKY_NOTE_WIDTH - 24)),
    y: Math.max(80, Math.min(viewportH / 3 + offset, viewportH - 200)),
  };
}

export function useStickyNotesStorage(user: unknown) {
  const userRef = useRef(user);
  const [notes, setNotes] = useState<StickyNote[]>(() => loadStickyNotes(user));

  useEffect(() => {
    if (userRef.current !== user) {
      userRef.current = user;
      setNotes(loadStickyNotes(user));
    }
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveStickyNotes(userRef.current, notes);
    }, 150);
    return () => clearTimeout(timer);
  }, [notes]);

  const addNote = useCallback((color: StickyNoteColor) => {
    setNotes(prev => {
      if (prev.length >= STICKY_NOTES_MAX) return prev;
      const pos = defaultPosition(prev.length);
      const created = createStickyNote(color, {
        x: pos.x,
        y: pos.y,
        zIndex: nextZIndex(prev),
      });
      return [...prev, created];
    });
  }, []);

  const updateNote = useCallback(
    (
      id: string,
      patch: Partial<Pick<StickyNote, 'text' | 'x' | 'y' | 'width' | 'height' | 'zIndex' | 'minimized'>>,
    ) => {
      setNotes(prev =>
        prev.map(note =>
          note.id === id
            ? { ...note, ...patch, updatedAt: new Date().toISOString() }
            : note,
        ),
      );
    },
    [],
  );

  const bringToFront = useCallback((id: string) => {
    setNotes(prev => {
      const maxZ = nextZIndex(prev) - 1;
      return prev.map(note =>
        note.id === id && note.zIndex < maxZ
          ? { ...note, zIndex: maxZ + 1, updatedAt: new Date().toISOString() }
          : note,
      );
    });
  }, []);

  const minimizeNote = useCallback((id: string) => {
    setNotes(prev =>
      prev.map(note =>
        note.id === id
          ? { ...note, minimized: true, updatedAt: new Date().toISOString() }
          : note,
      ),
    );
  }, []);

  const restoreNote = useCallback((id: string) => {
    setNotes(prev => {
      const maxZ = nextZIndex(prev);
      return prev.map(note =>
        note.id === id
          ? { ...note, minimized: false, zIndex: maxZ, updatedAt: new Date().toISOString() }
          : note,
      );
    });
  }, []);

  const removeNote = useCallback((id: string) => {
    setNotes(prev => prev.filter(note => note.id !== id));
  }, []);

  const openNotes = notes.filter(note => !note.minimized);
  const minimizedNotes = notes.filter(note => note.minimized);

  return {
    notes,
    openNotes,
    minimizedNotes,
    addNote,
    updateNote,
    bringToFront,
    minimizeNote,
    restoreNote,
    removeNote,
    canAddMore: notes.length < STICKY_NOTES_MAX,
  };
}
